// Decode JWT token to extract user info from Google credential
export interface GoogleUserInfo {
  sub: string;       // Google user ID
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
}

/**
 * Decode a Google JWT credential to extract user profile information
 */
export function decodeGoogleCredential(credential: string): GoogleUserInfo | null {
  try {
    // JWT is three base64 parts separated by dots; payload is the second part
    const base64Url = credential.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload) as GoogleUserInfo;
  } catch (e) {
    console.error('Failed to decode Google credential:', e);
    return null;
  }
}

interface GoogleAuthConfig {
  clientId: string;
}

class GoogleAuth {
  private clientId: string;
  private scriptLoading: Promise<void> | null = null;

  constructor(config: GoogleAuthConfig) {
    this.clientId = config.clientId;
  }

  /**
   * Load Google Identity Services script (ensures it's only loaded once)
   */
  loadScript(): Promise<void> {
    if (this.scriptLoading) return this.scriptLoading;

    this.scriptLoading = new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window not available'));
        return;
      }

      // Check if already loaded
      if ((window as any).google?.accounts?.id) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.scriptLoading = null;
        reject(new Error('Failed to load Google Identity Services script'));
      };
      document.head.appendChild(script);
    });

    return this.scriptLoading;
  }

  /**
   * Render a Google Sign-In button into a visible DOM element.
   * Returns the user info decoded from the credential JWT along with the credential token.
   */
  renderButton(
    elementOrId: HTMLElement | string | null,
    callback: (credential: string, userInfo?: GoogleUserInfo) => void,
    options?: { theme?: string; size?: string; text?: string; shape?: string; width?: string }
  ): void {
    const element = typeof elementOrId === 'string'
      ? document.getElementById(elementOrId)
      : elementOrId;

    if (!element) {
      console.error('Google button container element not found');
      return;
    }

    if (!(window as any).google?.accounts?.id) {
      console.error('Google Identity Services not loaded. Call loadScript() first.');
      return;
    }

    (window as any).google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: any) => {
        if (response.credential) {
          const userInfo = decodeGoogleCredential(response.credential);
          callback(response.credential, userInfo ?? undefined);
        }
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    (window as any).google.accounts.id.renderButton(element, {
      type: 'standard',
      theme: options?.theme || 'outline',
      size: options?.size || 'large',
      text: options?.text || 'signin_with',
      shape: options?.shape || 'rectangular',
      logo_alignment: 'left',
      width: options?.width || '300',
    });
  }

  /**
   * Trigger the Google account chooser popup.
   * This creates a hidden rendered button and programmatically clicks it.
   */
  prompt(callback?: (credential: string, userInfo?: GoogleUserInfo) => void): void {
    if (!(window as any).google?.accounts?.id) {
      console.error('Google Identity Services not loaded');
      return;
    }

    // Create a temporary container for the button
    const containerId = 'google-prompt-container-' + Date.now();
    const container = document.createElement('div');
    container.id = containerId;
    container.style.display = 'none';
    document.body.appendChild(container);

    // Set the callback and render the button into the hidden container
    (window as any).google.accounts.id.initialize({
      client_id: this.clientId,
      callback: (response: any) => {
        if (response.credential) {
          const userInfo = decodeGoogleCredential(response.credential);
          callback?.(response.credential, userInfo ?? undefined);
        }
        // Clean up the container
        setTimeout(() => container.remove(), 1000);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    (window as any).google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      width: '1',
    });

    // Trigger a click on the rendered Google button
    setTimeout(() => {
      const googleButton = container.querySelector('div[role="button"]') as HTMLElement;
      if (googleButton) {
        googleButton.click();
      } else {
        console.warn('[Google Auth] Could not find rendered button to click');
        container.remove();
      }
    }, 100);
  }

  /**
   * Sign out / disable auto select
   */
  signOut(): void {
    if ((window as any).google?.accounts?.id) {
      (window as any).google.accounts.id.disableAutoSelect();
    }
  }
}

// Create singleton instance
export const googleAuth = new GoogleAuth({
  clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
});

// Type declaration for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement | null, options: any) => void;
          prompt: (momentListener?: (moment: string) => void) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}